import React from 'react';
import { Link } from 'react-router-dom';
import { FadeIn } from '../components/FadeIn';
const sections = [{
  number: '01',
  heading: 'Identity & Belonging',
  paragraphs: ['Cognarchism defines identity through demonstrated alignment, not passive belief.', 'Followers of the system are known as:'],
  definitions: [{
    term: 'Cognarchists',
    desc: 'The default identity.'
  }, {
    term: 'Cognarchs',
    desc: 'The inner circle of demonstrated judgment.'
  }, {
    term: 'Sovereigns',
    desc: 'The highest expression of self-mastery and control.'
  }],
  afterDefinitions: ['This structure creates progression.', 'You do not declare status — you earn it.']
}, {
  number: '02',
  heading: 'Everyday Language',
  paragraphs: ['A living system requires language that is used, not just understood.', 'Cognarchists reinforce alignment through simple, repeatable signals:'],
  definitions: [{
    term: 'Aligned',
    desc: 'You are clear, stable, and coherent.'
  }, {
    term: 'Stay aligned',
    desc: 'Maintain discipline and clarity.'
  }, {
    term: 'Sharp',
    desc: 'Precise, effective thinking.'
  }, {
    term: 'Clear mind',
    desc: 'No distortion, no emotional fog.'
  }],
  afterDefinitions: ['These are not slogans.', 'They are micro-reinforcements of culture.']
}, {
  number: '03',
  heading: 'Core Doctrine (Compressed)',
  paragraphs: ['Cognarchism operates through high-density statements that encode its philosophy:'],
  doctrineList: ['Alignment over ideology', 'Judgment is power', 'Clarity is status', 'Earn your influence', 'You are what you can think through', "Reality doesn't care how you feel"],
  afterDefinitions: ['Each phrase replaces emotional narrative with structural truth.']
}, {
  number: '04',
  heading: 'System Language',
  paragraphs: ['To function at scale, the system requires precision vocabulary.'],
  definitions: [{
    term: 'Signal vs Noise',
    desc: 'Reality vs distortion.'
  }, {
    term: 'Cognitive Drift',
    desc: 'Gradual loss of clarity and control.'
  }, {
    term: 'Judgment Failure',
    desc: 'Breakdown in reasoning or decision integrity.'
  }, {
    term: 'Alignment Gap',
    desc: 'Distance between current state and required standard.'
  }, {
    term: 'Upgrade Cycle',
    desc: 'Continuous improvement, recalibration, and advancement.'
  }, {
    term: 'Cognitive Load Breach',
    desc: 'Collapse under pressure or complexity.'
  }],
  afterDefinitions: ['This language turns abstract failure into diagnosable conditions.']
}, {
  number: '05',
  heading: 'Identity Framing',
  paragraphs: ['Cognarchism rejects passive belief language.', 'Instead of: "I believe in Cognarchism"', 'Aligned individuals say:'],
  doctrineList: ["I'm aligned", 'I operate on Cognarchic principles', 'I optimize for judgment'],
  afterDefinitions: ['This is not ideology.', 'This is operational identity.']
}, {
  number: '06',
  heading: 'Oppositional Clarity',
  paragraphs: ['A system without contrast collapses.', 'Cognarchism defines what it rejects:'],
  definitions: [{
    term: 'Captured minds',
    desc: 'Externally controlled thinking.'
  }, {
    term: 'Noise-driven actors',
    desc: 'Reactive, unstable behavior.'
  }, {
    term: 'Low-resolution thinking',
    desc: 'Shallow, simplistic reasoning.'
  }, {
    term: 'Emotion-led decision makers',
    desc: 'Impulse over structure.'
  }],
  afterDefinitions: ['These terms are used with precision — not frequency.']
}, {
  number: '07',
  heading: 'The Highest Social Cost',
  paragraphs: ['In Cognarchic culture, the ultimate failure is to be Captured.', 'To be Captured is to lose ownership of your mind.', 'To be driven by noise instead of judgment.', 'To be unfit for influence, responsibility, or trust.', 'It is not disagreement.', 'It is disqualification.']
}, {
  number: '08',
  heading: 'The Narrative',
  paragraphs: ['The old world operated on noise.', 'It rewarded performance over judgment.', 'Emotion over discipline.', 'Popularity over competence.', 'It normalized confusion.', 'Cognarchism rejects that world entirely.', 'We are the first generation to treat clarity as infrastructure.', 'The first to treat judgment as a civic standard.', 'The first to demand alignment at scale.']
}, {
  number: '09',
  heading: 'The Movement',
  paragraphs: ['Cognarchism is not a party.', 'It is not a tribe.', 'It is not a belief system.', 'It is an Upgrade Cycle.', 'You do not join it.', 'You synchronize with it.']
}];
export function LanguageOfAlignment() {
  return <article data-id="element-420">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-cognarch-white border-b border-cognarch-border" data-id="element-421">
        <div className="max-w-3xl mx-auto px-6" data-id="element-422">
          <FadeIn delay={0.1} direction="down" data-id="element-423">
            <p className="uppercase tracking-[0.25em] text-xs font-medium text-cognarch-gray mb-8" data-id="element-424">
              Resource — Cultural System
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-425">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.1] tracking-tight text-cognarch-black mb-4 text-balance" data-id="element-426">
              Language of Alignment
            </h1>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-427">
            <p className="text-lg md:text-xl font-serif text-cognarch-charcoal/70 mb-8" data-id="element-428">
              The Cultural Operating System of Cognarchism
            </p>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-429">
            <div className="text-base md:text-lg text-cognarch-gray leading-relaxed max-w-2xl mb-12 space-y-4" data-id="element-430">
              <p data-id="element-431">Language shapes reality.</p>
              <p data-id="element-432">
                Cognarchism is not just a system of governance — it is a system
                of thought, identity, and expression.
              </p>
              <p data-id="element-433">This is how alignment becomes culture.</p>
              <p data-id="element-434">This is how a philosophy becomes a civilization.</p>
            </div>
          </FadeIn>

          <FadeIn delay={0.5} className="flex flex-col sm:flex-row gap-4" data-id="element-435">
            <Link to="/charter" className="inline-block px-7 py-3.5 bg-cognarch-black text-white font-medium tracking-wide hover:bg-cognarch-charcoal transition-all duration-300 text-sm text-center" data-id="element-436">
              Read the Manifesto
            </Link>
            <Link to="/overview" className="inline-block px-7 py-3.5 border border-cognarch-charcoal text-cognarch-charcoal font-medium tracking-wide hover:bg-cognarch-charcoal hover:text-white transition-all duration-300 text-sm text-center" data-id="element-437">
              Explore the Framework
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Sections */}
      {sections.map((section, index) => <section key={section.number} className={`py-20 md:py-28 border-b border-cognarch-border ${index % 2 === 1 ? 'bg-cognarch-offwhite' : 'bg-cognarch-white'}`} data-id="element-438">
          <div className="max-w-3xl mx-auto px-6" data-id="element-439">
            <FadeIn data-id="element-440">
              <div className="flex items-baseline gap-6 mb-8" data-id="element-441">
                <span className="text-cognarch-border font-serif text-4xl md:text-5xl leading-none select-none" data-id="element-442">
                  {section.number}
                </span>
                <h2 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-443">
                  {section.heading}
                </h2>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-444">
              <div className="space-y-6" data-id="element-445">
                {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-446">
                    {paragraph}
                  </p>)}

                {section.definitions && <div className="space-y-6 py-4 border-t border-cognarch-border mt-6 pt-6" data-id="element-447">
                    {section.definitions.map((def, dIndex) => <div key={dIndex} className="flex gap-4" data-id="element-448">
                        <span className="text-cognarch-charcoal/30 text-xs mt-1.5 shrink-0" data-id="element-449">
                          ◆
                        </span>
                        <div data-id="element-450">
                          <h3 className="text-cognarch-charcoal font-serif text-lg md:text-xl mb-1" data-id="element-451">
                            {def.term}
                          </h3>
                          <p className="text-cognarch-gray leading-[1.8] text-base" data-id="element-452">
                            {def.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>}

                {section.doctrineList && <div className="py-4 border-t border-cognarch-border mt-6 pt-6 space-y-3" data-id="element-453">
                    {section.doctrineList.map((item, lIndex) => <div key={lIndex} className="flex items-center gap-4 text-cognarch-charcoal font-serif text-lg md:text-xl" data-id="element-454">
                        <span className="text-cognarch-charcoal/30 text-xs shrink-0" data-id="element-455">
                          ◆
                        </span>
                        {item}
                      </div>)}
                  </div>}

                {section.afterDefinitions && <div className="space-y-4 mt-6" data-id="element-456">
                    {section.afterDefinitions.map((text, aIndex) => <p key={aIndex} className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-457">
                        {text}
                      </p>)}
                  </div>}
              </div>
            </FadeIn>
          </div>
        </section>)}

      {/* Closing Statement */}
      <section className="py-24 md:py-32 bg-cognarch-black text-white" data-id="element-458">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-459">
          <FadeIn data-id="element-460">
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-461" />
            <div className="space-y-4 text-xl md:text-2xl font-serif leading-relaxed text-white/80 mb-12 text-balance" data-id="element-462">
              <p data-id="element-463">This is how language becomes structure.</p>
              <p data-id="element-464">This is how structure becomes culture.</p>
              <p data-id="element-465">This is how culture becomes civilization.</p>
            </div>
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-466" />
            <p className="uppercase tracking-[0.25em] text-sm text-white/50 font-medium" data-id="element-467">
              Stay aligned.
            </p>
          </FadeIn>
        </div>
      </section>
    </article>;
}